/**
 * Central Error Handler for API Responses
 * Provides user-friendly error messages for different HTTP status codes
 */

export interface RateLimitInfo {
  allowed: boolean;
  global_used: number;
  global_limit: number;
  feature_used: number;
  feature_limit: number;
  resets_at: string;
  subscription_type: string;
  type?: 'subscription_limit' | 'bot_protection';
}

export interface ApiError {
  status: number;
  message: string;
  detail?: string;
  shouldRetry: boolean;
  retryAfterSeconds?: number;
  rateLimitInfo?: RateLimitInfo;
}

/**
 * Parse API error response and return user-friendly message
 */
export const handleApiError = async (response: Response): Promise<ApiError> => {
  const status = response.status;
  
  // Try to parse error detail from response
  let detail = '';
  let rateLimitInfo: RateLimitInfo | undefined;
  
  try {
    const errorData = await response.json();
    detail = errorData.detail || errorData.message || '';
    
    // Parse rate limit info if available
    if (status === 429 && errorData.info) {
      rateLimitInfo = {
        ...errorData.info,
        type: errorData.type
      };
    }
  } catch {
    // If JSON parsing fails, use status text
    detail = response.statusText;
  }

  // Handle different status codes
  switch (status) {
    case 429:
      // Rate Limited - parse specific limits and show user-friendly message
      let rateLimitMessage = 'Too many requests. Please wait and try again.';
      
      if (rateLimitInfo) {
        if (rateLimitInfo.type === 'bot_protection') {
          rateLimitMessage = 'Too many requests from your IP address. Please wait an hour.';
        } else if (rateLimitInfo.type === 'subscription_limit') {
          // Check which limit was hit
          const featureExceeded = rateLimitInfo.feature_used >= rateLimitInfo.feature_limit;
          const globalExceeded = rateLimitInfo.global_used >= rateLimitInfo.global_limit;
          
          if (featureExceeded && globalExceeded) {
            // Both limits hit
            rateLimitMessage = `Monthly limit reached (${rateLimitInfo.feature_used}/${rateLimitInfo.feature_limit} for this feature, ${rateLimitInfo.global_used}/${rateLimitInfo.global_limit} total). ` +
              `Upgrade your plan or wait until ${new Date(rateLimitInfo.resets_at).toLocaleDateString()}.`;
          } else if (globalExceeded) {
            // Only global limit hit (feature still has room)
            rateLimitMessage = `Total monthly limit reached (${rateLimitInfo.global_used}/${rateLimitInfo.global_limit}). ` +
              `Upgrade your plan or wait until ${new Date(rateLimitInfo.resets_at).toLocaleDateString()}.`;
          } else {
            // Only feature limit hit
            rateLimitMessage = `Monthly limit for this feature reached (${rateLimitInfo.feature_used}/${rateLimitInfo.feature_limit}). ` +
              `Upgrade your plan or wait until ${new Date(rateLimitInfo.resets_at).toLocaleDateString()}.`;
          }
        }
      }
      
      return {
        status: 429,
        message: rateLimitMessage,
        detail,
        shouldRetry: false,
        retryAfterSeconds: rateLimitInfo?.type === 'bot_protection' ? 3600 : undefined,
        rateLimitInfo
      };

    case 503:
      // Service Unavailable
      return {
        status: 503,
        message: 'Service is temporarily unavailable. Please try again in a moment.',
        detail,
        shouldRetry: true,
        retryAfterSeconds: 5
      };

    case 504:
      // Gateway Timeout
      return {
        status: 504,
        message: 'Request timed out. Please try again.',
        detail,
        shouldRetry: true,
        retryAfterSeconds: 3
      };

    case 500:
    case 502:
      // Server Error
      return {
        status,
        message: 'Server error occurred. Please try again later.',
        detail,
        shouldRetry: true,
        retryAfterSeconds: 5
      };

    case 401:
      // Unauthorized
      return {
        status: 401,
        message: 'Session expired. Please sign in again.',
        detail,
        shouldRetry: false
      };

    case 403:
      // Forbidden
      return {
        status: 403,
        message: "You don't have permission to perform this action.",
        detail,
        shouldRetry: false
      };

    case 404:
      // Not Found
      return {
        status: 404,
        message: 'Resource not found.',
        detail,
        shouldRetry: false
      };

    case 400:
      // Bad Request
      return {
        status: 400,
        message: detail || 'Invalid request. Please check your input.',
        detail,
        shouldRetry: false
      };

    default:
      return {
        status,
        message: 'An unexpected error occurred. Please try again.',
        detail,
        shouldRetry: false
      };
  }
};

/**
 * Format error message for display to user
 */
export const formatErrorMessage = (error: ApiError, includeDetail: boolean = false): string => {
  let message = error.message;
  
  if (error.retryAfterSeconds) {
    message += ` (Try again in ${error.retryAfterSeconds}s)`;
  }
  
  if (includeDetail && error.detail) {
    message += `\n\nDetails: ${error.detail}`;
  }
  
  return message;
};

/**
 * Simple toast notification utility
 */
export const showToast = (
  message: string, 
  type: 'success' | 'error' | 'info' = 'info',
  duration: number = 5000
): void => {
  // Create toast element
  const toast = document.createElement('div');
  toast.className = `fixed top-4 right-4 z-[10001] px-4 py-2 sm:px-6 sm:py-3 rounded-lg shadow-lg text-white text-sm sm:text-base animate-fade-in ${
    type === 'success' ? 'bg-green-500' : 
    type === 'error' ? 'bg-red-500' : 'bg-blue-500'
  }`;
  toast.textContent = message;
  toast.style.maxWidth = '400px';
  toast.style.wordWrap = 'break-word';
  
  document.body.appendChild(toast);
  
  // Auto-remove after duration
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.3s';
    setTimeout(() => toast.remove(), 300);
  }, duration);
};

